// Web Audio API metronome engine.
//
// Modes:
//   - 'single': uses this.pattern + this.barsEnabled (loop of N bars, each bar can be on/off)
//   - 'song':   walks through this.sections; each section has its own bpm/pattern/numBars
//
// Subdivisions: this.subdivision (1=quarter, 2=8th, 3=triplet, 4=16th).
// Sound types: 'square' | 'sine' | 'triangle' | 'noise'.

import { transportBus } from '@/lib/transportBus';
import { activeSounds } from '@/lib/activeSounds';
import { routeToMediaElement } from '@/lib/backgroundAudio';

const AHEAD_VISIBLE = 0.1;
const AHEAD_HIDDEN = 1.0;

export class MetronomeEngine {
  constructor(id = null, label = '') {
    this.id = id;
    this.label = label;
    this._runListeners = new Set();
    this.audioCtx = null;
    this.masterGain = null;
    // Timers get throttled with the screen locked: schedule further ahead while hidden.
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => { this.scheduleAheadTime = document.hidden ? AHEAD_HIDDEN : AHEAD_VISIBLE; });
    }

    this.lookahead = 25.0;
    this.scheduleAheadTime = 0.1;
    this.timerId = null;
    this._rafId = null;

    this.nextNoteTime = 0.0;
    this.currentBeat = 0;
    this.currentSubdiv = 0;
    this.currentBar = 0;
    this.currentSection = 0;

    // Single-mode params
    this.bpm = 120;
    this.pattern = [2, 1, 1, 1];
    this.barsEnabled = [true];

    // Common params
    this.subdivision = 1;
    this.soundType = 'square';
    this.volume = 0.7;

    // Song-mode params
    this.mode = 'single'; // or 'song'
    this.sections = [];
    this.loopSong = true;

    // Count-in (pre-roll)
    this.countInBars = 0;     // 0 = off; otherwise N bars of click before main playback

    // Practice ramp: increase BPM by `rampDelta` every `rampEveryBars` (capped at rampMaxBpm)
    this.rampEnabled = false;
    this.rampDelta = 2;
    this.rampEveryBars = 4;
    this.rampMaxBpm = 300;
    this._rampOffset = 0;     // added to bpm during playback
    this._barsCompleted = 0;  // bars elapsed since real playback started

    // Count-in runtime state
    this._inCountIn = false;
    this._countInBarsLeft = 0;

    // Sequencer mode: each bar can have its own subdivision + pattern
    // bar shape: { subdivision: 1|2|4, pattern: [0|1|2,...], muted: boolean }
    this.sequencerBars = [];
    this.sequencerBeatsPerBar = 4;
    this.seqBarIdx = 0;
    this.seqCellIdx = 0;

    this.notesInQueue = [];
    // Listener: (info) => void; info = { bar, beatInBar, section, time, isCountIn, countInBarsLeft }
    this.onBeat = null;
    this.onSongEnd = null;
    this.onCountInEnd = null;
    this.onBpmRamp = null;    // (effectiveBpm) => void
  }

  ensureContext() {
    if (!this.audioCtx) {
      const Ctor = window.AudioContext || window.webkitAudioContext;
      this.audioCtx = new Ctor();
      this.masterGain = this.audioCtx.createGain();
      this.masterGain.gain.value = this.volume;
      // Brick-wall-ish limiter so the +Boost (volume > 1.0) doesn't clip.
      this.limiter = this.audioCtx.createDynamicsCompressor();
      this.limiter.threshold.value = -1;   // dB just below 0 dBFS
      this.limiter.knee.value = 0;
      this.limiter.ratio.value = 20;
      this.limiter.attack.value = 0.003;
      this.limiter.release.value = 0.05;
      this.masterGain.connect(this.limiter);
      // Gate cuts clicks already scheduled ahead when stopping.
      this.gate = this.audioCtx.createGain();
      this.limiter.connect(this.gate);
      // Through a media element so it keeps playing with the phone locked.
      this._el = routeToMediaElement(this.audioCtx, this.gate);
      this.audioCtx.onstatechange = () => {
        if (this.timerId && this.audioCtx.state === 'suspended') this.audioCtx.resume().catch(() => {});
      };
    }
    if (this.audioCtx.state === 'suspended') {
      this.audioCtx.resume();
    }
    this._el?.play().catch(() => {});
  }

  setVolume(v) {
    this.volume = v;
    if (this.masterGain) {
      this.masterGain.gain.setTargetAtTime(v, this.audioCtx.currentTime, 0.01);
    }
  }

  setBpm(bpm) { this.bpm = bpm; this._bpmListeners?.forEach((fn) => fn(bpm)); if (this.timerId) this._announce(); }
  onBpm(fn) { (this._bpmListeners ||= new Set()).add(fn); return () => this._bpmListeners.delete(fn); }
  setPattern(pattern) { this.pattern = pattern; }
  setPatternB(pattern) { this.patternB = pattern; }
  setSoundTypeB(t) { this.soundTypeB = t; }
  setTrainerEnabled(b) { this.trainerEnabled = !!b; }
  setTrainerPlayBars(n) { this.trainerPlayBars = Math.max(1, n | 0); }
  setTrainerMuteBars(n) { this.trainerMuteBars = Math.max(1, n | 0); }
  setSequencerBars(arr) { this.sequencerBars = arr; }
  setSequencerBeatsPerBar(n) { this.sequencerBeatsPerBar = Math.max(1, n | 0); }
  setBarsEnabled(arr) { this.barsEnabled = arr; }
  setSubdivision(n) { this.subdivision = Math.max(1, n | 0); }
  setSoundType(t) { this.soundType = t; }
  // Drum kit mode: separate sounds per accent state. Pass null to clear (use main soundType).
  setSoundTypeForState(state, sound) {
    if (!this.soundTypeMap) this.soundTypeMap = {};
    if (sound == null) delete this.soundTypeMap[state];
    else this.soundTypeMap[state] = sound;
  }
  setMode(m) { this.mode = m; }
  setSections(arr) { this.sections = arr; }
  setLoopSong(b) { this.loopSong = !!b; }
  setCountInBars(n) { this.countInBars = Math.max(0, n | 0); }
  setRampEnabled(b) { this.rampEnabled = !!b; }
  setRampDelta(n) { this.rampDelta = n; }
  setRampEveryBars(n) { this.rampEveryBars = Math.max(1, n | 0); }
  setRampMaxBpm(n) { this.rampMaxBpm = n; }

  // current effective bpm (with ramp)
  effectiveBpm() {
    return Math.min(this.rampMaxBpm, this.bpm + this._rampOffset);
  }

  // Returns active params for the current scheduling step.
  _activeParams() {
    // During count-in, use a synthetic pattern derived from current beats-per-bar
    if (this._inCountIn) {
      const baseBeats = (this.mode === 'song' && this.sections.length > 0)
        ? (this.sections[0].pattern.length || 4)
        : (this.pattern.length || 4);
      // First beat accent, others click
      const ciPattern = Array.from({ length: baseBeats }, (_, i) => (i === 0 ? 2 : 1));
      return {
        bpm: this.bpm, // count-in always uses base bpm (no ramp)
        pattern: ciPattern,
        numBars: 1,
        barsEnabled: null,
      };
    }
    if (this.mode === 'song' && this.sections.length > 0) {
      const sec = this.sections[this.currentSection] || this.sections[0];
      return {
        bpm: Math.min(this.rampMaxBpm, sec.bpm + this._rampOffset),
        pattern: sec.pattern,
        numBars: sec.numBars,
        barsEnabled: null,
      };
    }
    return {
      bpm: Math.min(this.rampMaxBpm, this.bpm + this._rampOffset),
      pattern: this.pattern,
      numBars: this.barsEnabled.length || 1,
      barsEnabled: this.barsEnabled,
    };
  }

  // Helper: short noise burst with bandpass filter (noise buffer cached)
  _scheduleNoiseHit(time, { freq, q = 9, peakGain = 0.7, duration = 0.04, highpass = false }) {
    const ctx = this.audioCtx;
    if (!this._noiseBuf || this._noiseBuf.sampleRate !== ctx.sampleRate) {
      const size = Math.floor(ctx.sampleRate * 0.5);
      this._noiseBuf = ctx.createBuffer(1, size, ctx.sampleRate);
      const d = this._noiseBuf.getChannelData(0);
      for (let i = 0; i < size; i++) d[i] = Math.random() * 2 - 1;
    }
    const src = ctx.createBufferSource();
    src.buffer = this._noiseBuf;
    const filter = ctx.createBiquadFilter();
    filter.type = highpass ? 'highpass' : 'bandpass';
    filter.frequency.value = freq;
    filter.Q.value = q;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, time);
    gain.gain.exponentialRampToValueAtTime(peakGain, time + 0.001);
    gain.gain.exponentialRampToValueAtTime(0.0001, time + duration);
    src.connect(filter); filter.connect(gain); gain.connect(this.masterGain);
    src.start(time, Math.random() * 0.3); src.stop(time + duration + 0.02);
  }

  // Helper: oscillator hit with envelope, optional pitch sweep and filter
  _scheduleOscHit(time, { type = 'sine', freq, freqEnd = null, peakGain = 0.7, duration = 0.05, filter = null }) {
    const ctx = this.audioCtx;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, time);
    if (freqEnd !== null && freqEnd > 0) {
      osc.frequency.exponentialRampToValueAtTime(freqEnd, time + duration * 0.9);
    }
    gain.gain.setValueAtTime(0.0001, time);
    gain.gain.exponentialRampToValueAtTime(peakGain, time + 0.002);
    gain.gain.exponentialRampToValueAtTime(0.0001, time + duration);
    let out = osc;
    if (filter) {
      const f = ctx.createBiquadFilter();
      f.type = filter.type; f.frequency.value = filter.freq; f.Q.value = filter.q ?? 1;
      osc.connect(f); out = f;
    }
    out.connect(gain); gain.connect(this.masterGain);
    osc.start(time); osc.stop(time + duration + 0.02);
  }

  // type: 0=silent, 1=normal click, 2=accent, 3=high accent (più acuto / sky), 4=subdivision tick (internal)
  // soundType override (defaults to this.soundType for voice A)
  scheduleClick(type, time, soundType) {
    if (type === 0 || !this.audioCtx) return;
    transportBus.emitClick(time - this.audioCtx.currentTime);
    // Sound resolution priority: explicit override > per-state kit > master soundType
    const kitSound = this.soundTypeMap && this.soundTypeMap[type];
    const sound = soundType || kitSound || this.soundType;
    const ctx = this.audioCtx;
    const isAccent = type === 2;
    const isHigh = type === 3;
    const isSub = type === 4;

    // Loudness factor per accent type — keeps voicing consistent across sounds.
    const accentBoost = isHigh ? 1.4 : isAccent ? 1.3 : isSub ? 0.45 : 1.0;
    const pitchBoost = isHigh ? 1.5 : isAccent ? 1.15 : isSub ? 0.85 : 1.0;

    // === Custom synthesised percussion ===
    if (sound === 'cowbell') {
      // 808-style: two detuned squares through a bandpass, long decay
      const f1 = (isHigh ? 680 : 562) * pitchBoost;
      const bp = { type: 'bandpass', freq: 2640, q: 1.2 };
      this._scheduleOscHit(time, { type: 'square', freq: f1, peakGain: 0.5 * accentBoost, duration: 0.3, filter: bp });
      this._scheduleOscHit(time, { type: 'square', freq: f1 * 1.505, peakGain: 0.45 * accentBoost, duration: 0.3, filter: bp });
      return;
    }
    if (sound === 'rimshot') {
      this._scheduleOscHit(time, { type: 'triangle', freq: 1700 * pitchBoost, peakGain: 0.5 * accentBoost, duration: 0.03 });
      this._scheduleOscHit(time, { type: 'sine', freq: 455 * pitchBoost, peakGain: 0.4 * accentBoost, duration: 0.04 });
      this._scheduleNoiseHit(time, { freq: 2600, q: 3, peakGain: 0.35 * accentBoost, duration: 0.03 });
      return;
    }
    if (sound === 'bell') {
      // Inharmonic bell partials, steady pitch with long ring
      const base = (isHigh ? 1320 : 880) * pitchBoost;
      this._scheduleOscHit(time, { type: 'sine', freq: base, peakGain: 0.5 * accentBoost, duration: 0.9 });
      this._scheduleOscHit(time, { type: 'sine', freq: base * 2.76, peakGain: 0.22 * accentBoost, duration: 0.55 });
      this._scheduleOscHit(time, { type: 'sine', freq: base * 5.4, peakGain: 0.1 * accentBoost, duration: 0.3 });
      this._scheduleNoiseHit(time, { freq: base * 4, q: 6, peakGain: 0.08 * accentBoost, duration: 0.01 });
      return;
    }
    if (sound === 'claves') {
      this._scheduleOscHit(time, { type: 'sine', freq: 2480 * pitchBoost, freqEnd: 2350 * pitchBoost, peakGain: 0.75 * accentBoost, duration: 0.07 });
      this._scheduleNoiseHit(time, { freq: 5000, q: 4, peakGain: 0.12 * accentBoost, duration: 0.008 });
      return;
    }
    if (sound === 'snare') {
      this._scheduleNoiseHit(time, { freq: 1200, q: 0.7, peakGain: 0.5 * accentBoost, duration: 0.16, highpass: true });
      this._scheduleNoiseHit(time, { freq: 5000, q: 1, peakGain: 0.2 * accentBoost, duration: 0.1 });
      this._scheduleOscHit(time, { type: 'triangle', freq: 190 * pitchBoost, freqEnd: 150, peakGain: 0.5 * accentBoost, duration: 0.09 });
      this._scheduleOscHit(time, { type: 'sine', freq: 330 * pitchBoost, freqEnd: 280, peakGain: 0.25 * accentBoost, duration: 0.06 });
      return;
    }
    if (sound === 'hihat') {
      // Metallic: inharmonic squares through a highpass (TR-808 ratios) + noise sizzle
      const dur = isAccent || isHigh ? 0.09 : 0.045;
      const hp = { type: 'highpass', freq: 7000, q: 0.7 };
      [2, 3, 4.16, 5.43, 6.79, 8.21].forEach((r) => {
        this._scheduleOscHit(time, { type: 'square', freq: 205 * r, peakGain: 0.07 * accentBoost, duration: dur, filter: hp });
      });
      this._scheduleNoiseHit(time, { freq: 8000, q: 1, peakGain: 0.25 * accentBoost, duration: dur, highpass: true });
      return;
    }
    if (sound === 'tom') {
      this._scheduleOscHit(time, { type: 'sine', freq: 170 * pitchBoost, freqEnd: 95, peakGain: 0.9 * accentBoost, duration: 0.3 });
      this._scheduleOscHit(time, { type: 'triangle', freq: 340 * pitchBoost, freqEnd: 190, peakGain: 0.15 * accentBoost, duration: 0.12 });
      this._scheduleNoiseHit(time, { freq: 600, q: 2, peakGain: 0.15 * accentBoost, duration: 0.03 });
      return;
    }
    if (sound === 'block') {
      // Woodblock: hollow resonant body + short wooden knock
      const f = (isHigh ? 1250 : 1000) * pitchBoost;
      this._scheduleOscHit(time, { type: 'sine', freq: f, peakGain: 0.65 * accentBoost, duration: 0.07 });
      this._scheduleOscHit(time, { type: 'triangle', freq: f * 2.1, peakGain: 0.2 * accentBoost, duration: 0.03 });
      this._scheduleNoiseHit(time, { freq: f * 1.5, q: 12, peakGain: 0.2 * accentBoost, duration: 0.015 });
      return;
    }
    if (sound === 'ping') {
      this._scheduleOscHit(time, { type: 'sine', freq: (isHigh ? 3200 : 2200) * pitchBoost, freqEnd: 2000 * pitchBoost, peakGain: 0.65 * accentBoost, duration: 0.22 });
      this._scheduleOscHit(time, { type: 'sine', freq: (isHigh ? 6400 : 4400) * pitchBoost, peakGain: 0.08 * accentBoost, duration: 0.06 });
      return;
    }
    if (sound === 'kick') {
      this._scheduleOscHit(time, { type: 'sine', freq: 150 * pitchBoost, freqEnd: 42, peakGain: 1.0 * accentBoost, duration: 0.32 });
      this._scheduleNoiseHit(time, { freq: 3000, q: 1.5, peakGain: 0.2 * accentBoost, duration: 0.006 });
      return;
    }

    // === Standard oscillator/noise click ===
    let freq, peakGain, duration;
    if (isHigh) { freq = 2700; peakGain = 1.0; duration = 0.045; }
    else if (isAccent) { freq = 1800; peakGain = 0.95; duration = 0.05; }
    else if (isSub) { freq = 700; peakGain = 0.22; duration = 0.025; }
    else { freq = 900; peakGain = 0.55; duration = 0.05; }

    if (sound === 'noise') {
      this._scheduleNoiseHit(time, {
        freq: isHigh ? 3400 : isAccent ? 2400 : isSub ? 1500 : 1900,
        q: 9,
        peakGain: peakGain * 1.4,
        duration,
      });
      return;
    }

    // sine | triangle | square | sawtooth — built-in oscillator types
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = sound;
    osc.frequency.setValueAtTime(freq, time);
    osc.frequency.exponentialRampToValueAtTime(freq * 0.55, time + duration * 0.7);
    gain.gain.setValueAtTime(0.0001, time);
    gain.gain.exponentialRampToValueAtTime(peakGain, time + 0.001);
    gain.gain.exponentialRampToValueAtTime(0.0001, time + duration);
    osc.connect(gain); gain.connect(this.masterGain);
    osc.start(time);
    osc.stop(time + duration + 0.02);
    // Short transient gives every basic click a crisper attack
    this._scheduleNoiseHit(time, { freq: freq * 3, q: 2, peakGain: peakGain * 0.18, duration: 0.006 });
  }

  scheduleClickB(type, time) {
    if (type === 0 || !this.audioCtx) return;
    transportBus.emitClick(time - this.audioCtx.currentTime);
    const ctx = this.audioCtx;
    const isAccent = type === 2;

    const freq = isAccent ? 520 : 320;       // distinctly lower than voice A
    const peakGain = isAccent ? 0.7 : 0.42;
    const duration = 0.06;

    if (this.soundTypeB === 'noise') {
      const bufferSize = Math.floor(ctx.sampleRate * (duration + 0.01));
      const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
      const data = buffer.getChannelData(0);
      for (let i = 0; i < bufferSize; i++) data[i] = Math.random() * 2 - 1;
      const src = ctx.createBufferSource();
      src.buffer = buffer;
      const filter = ctx.createBiquadFilter();
      filter.type = 'bandpass';
      filter.frequency.value = isAccent ? 900 : 600;
      filter.Q.value = 10;
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0.0001, time);
      gain.gain.exponentialRampToValueAtTime(peakGain * 1.3, time + 0.001);
      gain.gain.exponentialRampToValueAtTime(0.0001, time + duration);
      src.connect(filter); filter.connect(gain); gain.connect(this.masterGain);
      src.start(time);
      src.stop(time + duration + 0.02);
      return;
    }

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = this.soundTypeB || 'sine';
    osc.frequency.setValueAtTime(freq, time);
    osc.frequency.exponentialRampToValueAtTime(freq * 0.6, time + duration * 0.7);
    gain.gain.setValueAtTime(0.0001, time);
    gain.gain.exponentialRampToValueAtTime(peakGain, time + 0.001);
    gain.gain.exponentialRampToValueAtTime(0.0001, time + duration);
    osc.connect(gain); gain.connect(this.masterGain);
    osc.start(time);
    osc.stop(time + duration + 0.02);
  }

  // Sequencer mode scheduler: iterates per-bar with per-bar subdivision.
  _scheduleSequencer() {
    while (this.nextNoteTime < this.audioCtx.currentTime + this.scheduleAheadTime) {
      const bars = this.sequencerBars;
      if (!bars || bars.length === 0) break;
      const bar = bars[this.seqBarIdx % bars.length];
      if (!bar || !bar.pattern || bar.pattern.length === 0) break;

      const cellsInBar = bar.pattern.length;
      const beatsPerBar = bar.beatsPerBar || this.sequencerBeatsPerBar || 4;
      const effBpm = Math.min(this.rampMaxBpm, this.bpm + this._rampOffset);
      const barDuration = (60.0 / Math.max(1, effBpm)) * beatsPerBar;
      const cellDuration = barDuration / cellsInBar;

      const cellState = bar.muted ? 0 : (bar.pattern[this.seqCellIdx] ?? 0);
      this.scheduleClick(cellState, this.nextNoteTime);

      this.notesInQueue.push({
        bar: this.seqBarIdx,
        cell: this.seqCellIdx,
        time: this.nextNoteTime,
        sequencer: true,
      });
      if (this.seqCellIdx === 0) this._emitBar();

      this.nextNoteTime += cellDuration;

      // Advance cell, then bar
      this.seqCellIdx += 1;
      if (this.seqCellIdx >= cellsInBar) {
        this.seqCellIdx = 0;
        this._barsCompleted += 1;

        // Practice ramp on bar boundary
        if (this.rampEnabled && this._barsCompleted > 0 &&
            this._barsCompleted % this.rampEveryBars === 0) {
          const currentEff = this.bpm + this._rampOffset;
          if (currentEff < this.rampMaxBpm) {
            const proposedEff = currentEff + this.rampDelta;
            const cappedEff = Math.min(this.rampMaxBpm, proposedEff);
            this._rampOffset = cappedEff - this.bpm;
            if (this.onBpmRamp) this.onBpmRamp(cappedEff);
          }
        }

        this.seqBarIdx = (this.seqBarIdx + 1) % bars.length;
      }
    }
  }

  scheduler() {
    if (this.mode === 'sequencer') {
      this._scheduleSequencer();
      return;
    }
    while (this.nextNoteTime < this.audioCtx.currentTime + this.scheduleAheadTime) {
      const { bpm, pattern, numBars, barsEnabled } = this._activeParams();
      const beatsPerBar = pattern.length || 1;
      const subdiv = this.subdivision || 1;

      const isMain = this.currentSubdiv === 0;
      const barOn = barsEnabled ? barsEnabled[this.currentBar] !== false : true;
      const beatState = pattern[this.currentBeat] ?? 0;

      let type = 0;
      if (isMain) {
        type = barOn ? beatState : 0;
      } else {
        // subdivision tick: quieter, only when main beat is audible
        type = (barOn && beatState !== 0) ? 4 : 0;
      }

      this.scheduleClick(type, this.nextNoteTime);

      // Polyrhythm: at the start of each bar (currentBeat=0, isMain), pre-schedule voice B
      if (this.mode === 'polyrhythm' && isMain && this.currentBeat === 0 && !this._inCountIn) {
        // Trainer: mute voice B during the mute phase
        let voiceBMuted = false;
        if (this.trainerEnabled && this.trainerPlayBars > 0 && this.trainerMuteBars > 0) {
          const cycleLen = this.trainerPlayBars + this.trainerMuteBars;
          const phaseBar = this._barsCompleted % cycleLen;
          voiceBMuted = phaseBar >= this.trainerPlayBars;
        }
        if (!voiceBMuted) {
          const barDuration = (60.0 / Math.max(1, bpm)) * beatsPerBar;
          const beatsB = (this.patternB && this.patternB.length) || 0;
          for (let i = 0; i < beatsB; i++) {
            const tickTime = this.nextNoteTime + (i / beatsB) * barDuration;
            const tType = this.patternB[i] ?? 0;
            this.scheduleClickB(tType, tickTime);
            this.notesInQueueB.push({ beatIndex: i, time: tickTime });
          }
        }
      }

      if (isMain) {
        this.notesInQueue.push({
          bar: this.currentBar,
          beatInBar: this.currentBeat,
          section: this.currentSection,
          time: this.nextNoteTime,
          isCountIn: this._inCountIn,
          countInBarsLeft: this._countInBarsLeft,
          totalBars: this._barsCompleted,
        });
        if (this.currentBeat === 0 && !this._inCountIn) this._emitBar();
      }

      // Advance time by one subdivision step
      const secPerBeat = 60.0 / Math.max(1, bpm);
      this.nextNoteTime += secPerBeat / subdiv;

      // Advance pointers: subdivision -> beat -> bar -> (song) section
      this.currentSubdiv += 1;
      if (this.currentSubdiv >= subdiv) {
        this.currentSubdiv = 0;
        this.currentBeat += 1;
        if (this.currentBeat >= beatsPerBar) {
          this.currentBeat = 0;

          // Bar boundary. Handle count-in vs main playback.
          if (this._inCountIn) {
            this._countInBarsLeft -= 1;
            if (this._countInBarsLeft <= 0) {
              this._inCountIn = false;
              this.currentBar = 0;
              this.currentSection = 0;
              this._barsCompleted = 0;
              if (this.onCountInEnd) this.onCountInEnd();
            }
            // stay at bar 0 during count-in
            continue;
          }

          this.currentBar += 1;
          this._barsCompleted += 1;

          // Practice ramp: every N completed bars, bump bpm offset (only ramps UP)
          if (this.rampEnabled && this._barsCompleted > 0 &&
              this._barsCompleted % this.rampEveryBars === 0) {
            const baseBpm = (this.mode === 'song' && this.sections.length > 0)
              ? (this.sections[this.currentSection]?.bpm || this.bpm)
              : this.bpm;
            const currentEff = baseBpm + this._rampOffset;
            // already at or above cap → do nothing
            if (currentEff < this.rampMaxBpm) {
              const proposedEff = currentEff + this.rampDelta;
              const cappedEff = Math.min(this.rampMaxBpm, proposedEff);
              this._rampOffset = cappedEff - baseBpm;
              if (this.onBpmRamp) this.onBpmRamp(cappedEff);
            }
          }

          if (this.currentBar >= numBars) {
            this.currentBar = 0;
            if (this.mode === 'song' && this.sections.length > 0) {
              this.currentSection += 1;
              if (this.currentSection >= this.sections.length) {
                if (this.loopSong) {
                  this.currentSection = 0;
                } else {
                  this.currentSection = this.sections.length - 1;
                  this._songEndScheduledAt = this.nextNoteTime;
                }
              }
            }
          }
        }
      }
    }
  }

  start() {
    this.ensureContext();
    if (this.timerId) return;
    this.currentBeat = 0;
    this.currentSubdiv = 0;
    this.currentBar = 0;
    this.currentSection = 0;
    this.seqBarIdx = 0;
    this.seqCellIdx = 0;
    this._songEndScheduledAt = null;
    this._rampOffset = 0;
    this._barsCompleted = 0;
    if (this.countInBars > 0) {
      this._inCountIn = true;
      this._countInBarsLeft = this.countInBars;
    } else {
      this._inCountIn = false;
      this._countInBarsLeft = 0;
    }
    this.nextNoteTime = this.audioCtx.currentTime + 0.05;
    this.scheduleAheadTime = document.hidden ? AHEAD_HIDDEN : AHEAD_VISIBLE;
    this.gate.gain.cancelScheduledValues(this.audioCtx.currentTime);
    this.gate.gain.setValueAtTime(1, this.audioCtx.currentTime);
    this.notesInQueue = [];
    this.notesInQueueB = [];
    // Drained from both the timer and rAF so beat callbacks keep flowing with the screen locked.
    const flush = () => {
      const now = this.audioCtx.currentTime;
      while (this.notesInQueue.length && this.notesInQueue[0].time <= now) {
        const note = this.notesInQueue.shift();
        if (this.onBeat) this.onBeat(note);
      }
      while (this.notesInQueueB.length && this.notesInQueueB[0].time <= now) {
        const note = this.notesInQueueB.shift();
        if (this.onBeatB) this.onBeatB(note);
      }
    };
    this.timerId = setInterval(() => {
      flush();
      this.scheduler();
      // auto-stop when song ends without loop
      if (this._songEndScheduledAt && this.audioCtx.currentTime >= this._songEndScheduledAt) {
        this.stop();
        if (this.onSongEnd) this.onSongEnd();
      }
    }, this.lookahead);

    const drainQueue = () => {
      if (!this.timerId) return;
      flush();
      this._rafId = requestAnimationFrame(drainQueue);
    };
    this._rafId = requestAnimationFrame(drainQueue);
    transportBus.emitStart(this.id);
    this._announce();
    this._runListeners.forEach((fn) => fn(true));
  }

  stop() {
    if (this.timerId) {
      clearInterval(this.timerId);
      this.timerId = null;
      this.gate.gain.cancelScheduledValues(this.audioCtx.currentTime);
      this.gate.gain.setTargetAtTime(0, this.audioCtx.currentTime, 0.005);
      transportBus.emitStop(this.id);
      if (this.id) activeSounds.remove(this.id);
      this._runListeners.forEach((fn) => fn(false));
    }
    if (this._rafId) {
      cancelAnimationFrame(this._rafId);
      this._rafId = null;
    }
    this.notesInQueue = [];
    this.notesInQueueB = [];
    this.currentBeat = 0;
    this.currentSubdiv = 0;
    this.currentBar = 0;
    this.currentSection = 0;
    this.seqBarIdx = 0;
    this.seqCellIdx = 0;
    this._songEndScheduledAt = null;
    this._inCountIn = false;
    this._countInBarsLeft = 0;
    this._rampOffset = 0;
    this._barsCompleted = 0;
  }

  isRunning() { return !!this.timerId; }

  onRunning(fn) { this._runListeners.add(fn); return () => this._runListeners.delete(fn); }

  _announce() {
    if (this.id) activeSounds.set(this.id, { label: this.label, detail: `${Math.round(this.bpm)} BPM`, stop: () => this.stop() });
  }

  // Announced at scheduling time with the lead (s) before the downbeat sounds, so listeners can quantize.
  _emitBar() {
    const c = this.audioCtx;
    transportBus.emitBar({ id: this.id, lead: this.nextNoteTime - c.currentTime, latency: c.outputLatency || c.baseLatency || 0 });
  }
}
